import React from 'react';
import { AlertCircle } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';

const DeleteConfirmationModal = ({ deleteConfirmation, onConfirm, onCancel }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  if (!deleteConfirmation) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="relative max-w-md w-full mx-4">
        {/* Modal Background */}
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-br from-gray-900/40 via-gray-800/30 to-gray-900/40 border border-white/20 backdrop-blur-2xl'
          : 'bg-white border border-slate-200'
        } rounded-3xl ${isDarkMode ? 'shadow-[0_20px_50px_rgba(0,0,0,0.6)]' : 'shadow-lg'}`}></div>
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
          : ''
        } rounded-3xl`}></div>
        
        <div className="relative p-6">
          <div className="flex items-center mb-6">
            {/* Warning Icon */}
            <div className="relative group/warning mr-3">
              <div className={`${isDarkMode ? '' : 'hidden'} absolute inset-0 bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-2xl blur-sm`}></div>
              <div className={`relative w-10 h-10 rounded-2xl flex items-center justify-center border ${isDarkMode ? 'bg-gradient-to-br from-red-600/60 via-red-700/50 to-red-800/60 backdrop-blur-xl shadow-[0_8px_32px_rgba(239,68,68,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border-white/20' : 'bg-red-50 border-red-200'}`}>
                <AlertCircle className={`w-5 h-5 ${isDarkMode ? 'text-white/90 drop-shadow-lg' : 'text-red-600'}`} />
                <div className={`${isDarkMode ? '' : 'hidden'} absolute top-1 left-1 w-2.5 h-2.5 bg-white/30 rounded-full blur-sm`}></div>
              </div>
            </div>
            <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-900'}`}>{t('admin.deleteUser.title')}</h3>
          </div>
          
          <p className={`mb-6 ${isDarkMode ? 'text-white/80 drop-shadow-sm' : 'text-slate-700'}`}>
            {t('admin.deleteUser.confirm', { type: deleteConfirmation.isOci ? 'OCI-' : '', username: deleteConfirmation.username })}
          </p>
          
          <div className="flex justify-end space-x-3">
            {/* Cancel Button */}
            <button
              onClick={onCancel}
              className={`relative group/cancel overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] ${isDarkMode ? '' : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200'}`}
              style={isDarkMode ? {
                background: 'linear-gradient(135deg, rgba(71,85,105,0.6) 0%, rgba(75,85,99,0.6) 50%, rgba(71,85,105,0.6) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.3)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
              } : {}}
            >
              <div
                className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/cancel:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                style={isDarkMode ? {
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 100%)'
                } : {}}
              ></div>

              <div className={`relative z-10 px-4 py-2 flex items-center justify-center font-medium transition-all duration-300 ${isDarkMode ? 'text-white drop-shadow-lg group-hover/cancel:drop-shadow-xl' : ''}`}>
                {t('common.cancel')}
              </div>

              <div className={`absolute inset-0 opacity-0 group-hover/cancel:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                <div
                  className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/cancel:translate-x-full transition-transform duration-800"
                  style={isDarkMode ? {
                    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)'
                  } : {}}
                ></div>
              </div>
            </button>

            {/* Delete Button */}
            <button
              onClick={() => onConfirm(deleteConfirmation.id, deleteConfirmation.isOci)}
              className={`relative group/delete overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] ${isDarkMode ? '' : 'bg-red-600 hover:bg-red-700 text-white shadow-sm'}`}
              style={isDarkMode ? {
                background: 'linear-gradient(135deg, rgba(239,68,68,0.7) 0%, rgba(220,38,38,0.7) 50%, rgba(185,28,28,0.7) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.3)',
                boxShadow: '0 8px 32px rgba(239,68,68,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
              } : {}}
            >
              <div
                className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/delete:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                style={isDarkMode ? {
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 100%)'
                } : {}}
              ></div>

              <div className={`relative z-10 px-4 py-2 flex items-center justify-center font-medium text-white transition-all duration-300 ${isDarkMode ? 'drop-shadow-lg group-hover/delete:drop-shadow-xl' : ''}`}>
                {t('common.delete')}
              </div>

              <div className={`absolute inset-0 opacity-0 group-hover/delete:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                <div
                  className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/delete:translate-x-full transition-transform duration-800"
                  style={isDarkMode ? {
                    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)'
                  } : {}}
                ></div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmationModal;